import type { ReactElement } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Boxes, Building2, LayoutGrid, Link as LinkIcon, Map, Users } from "lucide-react";
import { AdminShell, type AdminNavItem } from "@ux/compositions";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { useSession } from "@shell/session/hooks";
import { FloorplanPage } from "../features/floorplan/FloorplanPage";
import { ViewOnlyBanner } from "./common/ViewOnlyBanner";
import { SageOfficesPage } from "./sage-offices/SageOfficesPage";
import { WorkspacesPage } from "./workspaces/WorkspacesPage";
import { InventoryPage } from "./workspaces/InventoryPage";
import { BulkImportWorkspacesPage } from "./workspaces/bulkImport/BulkImportWorkspacesPage";
import { AssetsPage } from "./assets/AssetsPage";
import { BulkImportAssetsPage } from "./assets/bulkImport/BulkImportAssetsPage";
import { AssetAssignmentsPage } from "./asset-assignments/AssetAssignmentsPage";
import { WorkspaceAssignmentsPage } from "./workspace-assignments/WorkspaceAssignmentsPage";

const navItems: readonly AdminNavItem[] = [
    {
        to: "/admin/sage-offices",
        label: "Sage offices",
        shortLabel: "Offices",
        icon: <Building2 size={16} />,
    },
    {
        to: "/admin/floorplan",
        label: "Floor plan",
        shortLabel: "Map",
        icon: <Map size={16} />,
    },
    {
        to: "/admin/workspaces",
        label: "Workspaces",
        shortLabel: "Spaces",
        icon: <LayoutGrid size={16} />,
    },
    {
        to: "/admin/assets",
        label: "Assets",
        shortLabel: "Assets",
        icon: <Boxes size={16} />,
    },
    {
        to: "/admin/asset-assignments",
        label: "Assignments",
        shortLabel: "Assigned",
        icon: <LinkIcon size={16} />,
    },
    {
        to: "/admin/workspace-assignments",
        label: "Occupants",
        shortLabel: "Occupants",
        icon: <Users size={16} />,
    },
];

export function AdminRoutes(): ReactElement {
    const session = useSession();
    const canEdit = useCanEdit();
    // Hide admin chrome for non-Editors. Do not force them onto floor plan —
    // that only happens when they opened a floor-plan URL before signing in.
    const showNav = session.isSuccess && canEdit;

    return (
        <Routes>
            <Route
                element={
                    <AdminShell
                        title="Office admin"
                        navItems={showNav ? navItems : []}
                        footer={showNav ? "Galileo · Office" : undefined}
                        showNav={showNav}
                    />
                }
            >
                <Route element={<AdminContent />}>
                    <Route index element={<Navigate to="sage-offices" replace />} />
                    <Route path="sage-offices" element={<SageOfficesPage />} />
                    <Route path="floorplan" element={<FloorplanPage />} />
                    <Route path="floorplan/:officeId" element={<FloorplanPage />} />
                    <Route path="workspaces" element={<WorkspacesPage />} />
                    <Route path="workspaces/bulk-import" element={<BulkImportWorkspacesPage />} />
                    <Route
                        path="workspaces/:workspaceId/inventory"
                        element={<InventoryPage />}
                    />
                    <Route path="assets" element={<AssetsPage />} />
                    <Route path="assets/bulk-import" element={<BulkImportAssetsPage />} />
                    <Route path="asset-assignments" element={<AssetAssignmentsPage />} />
                    <Route path="workspace-assignments" element={<WorkspaceAssignmentsPage />} />
                    <Route path="*" element={<Navigate to="sage-offices" replace />} />
                </Route>
            </Route>
        </Routes>
    );
}

/**
 * Sits between the AdminShell layout (sidebar/tab-bar) and the routed
 * page so the view-only banner can render above every admin page
 * without modifying the shared AdminShell composition or having each
 * page import the banner itself.
 */
function AdminContent(): ReactElement {
    return (
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <ViewOnlyBanner />
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <Outlet />
            </div>
        </div>
    );
}
